import blockParty1v0NoAccessoriesSvgSource from '../assets/fields/Block_Party_1v0_no_accessories.svg?raw';
import blockParty1v0SvgSource from '../assets/fields/Block_Party_1v0.svg?raw';
import blockPartySharedNoAccessoriesSvgSource from '../assets/fields/Block_Party_1v1_2v0_no_accessories.svg?raw';
import blockPartySharedSvgSource from '../assets/fields/Block_Party_1v1_2v0.svg?raw';
import competitionFieldNoAccessoriesSvgSource from '../assets/fields/Competition_Field_1_no_accessories.svg?raw';
import competitionFieldSvgSource from '../assets/fields/Competition_Field_1.svg?raw';
import launchParty1v0NoAccessoriesSvgSource from '../assets/fields/Launch_Party_1v0_no_accessories.svg?raw';
import launchParty1v0SvgSource from '../assets/fields/Launch_Party_1v0.svg?raw';
import launchParty1v1NoAccessoriesSvgSource from '../assets/fields/Launch_Party_1v1_no_accessories.svg?raw';
import launchParty1v1SvgSource from '../assets/fields/Launch_Party_1v1.svg?raw';
import launchParty2v0NoAccessoriesSvgSource from '../assets/fields/Launch_Party_2v0_no_accessories.svg?raw';
import launchParty2v0SvgSource from '../assets/fields/Launch_Party_2v0.svg?raw';

export type FieldFamily = 'launch-party' | 'competition-field' | 'block-party';
export type FieldVariant = '1v0' | '1v1' | '2v0' | 'competition';
export type FieldAssetKey =
  | 'launch-party-1v0'
  | 'launch-party-1v1'
  | 'launch-party-2v0'
  | 'competition-field'
  | 'block-party-1v0'
  | 'block-party-shared';

export interface FieldSelection {
  family: FieldFamily;
  variant: FieldVariant;
  accessoriesEnabled: boolean;
}

export interface FieldOption {
  family: FieldFamily;
  familyLabel: string;
  variant: FieldVariant;
  label: string;
  assetKey: FieldAssetKey;
  accessoriesSupported: boolean;
}

interface FieldFamilyManifestEntry {
  value: FieldFamily;
  label: string;
  order: number;
}

interface FieldManifestEntry {
  family: FieldFamily;
  variant: FieldVariant;
  label: string;
  assetKey: FieldAssetKey;
  accessoriesSupported: boolean;
}

export const DEFAULT_FIELD_SELECTION: FieldSelection = {
  family: 'launch-party',
  variant: '1v0',
  accessoriesEnabled: true,
};

export const FIELD_FAMILY_MANIFEST: FieldFamilyManifestEntry[] = [
  { value: 'launch-party', label: 'field.family.launchParty', order: 0 },
  { value: 'block-party', label: 'field.family.blockParty', order: 1 },
  { value: 'competition-field', label: 'field.family.competition', order: 2 },
];

/** English default for each `field.family.*` key (second arg to `t()` so Locize shows source text). */
export const FIELD_FAMILY_LABEL_DEFAULT: Record<FieldFamily, string> = {
  'launch-party': 'Launch Party',
  'block-party': 'Block Party',
  'competition-field': 'Competition',
};

export const FIELD_VARIANT_LABELS: Record<FieldVariant, string> = {
  '1v0': 'field.variant.onePlayer',
  '1v1': 'field.variant.1v1',
  '2v0': 'field.variant.twoPlayer',
  competition: 'field.variant.10x10Grid',
};

export const FIELD_VARIANT_LABEL_DEFAULT: Record<FieldVariant, string> = {
  '1v0': '1 Player',
  '1v1': '1v1',
  '2v0': '2 Player',
  competition: '10x10 Grid',
};

const FIELD_FAMILY_LABELS = Object.fromEntries(FIELD_FAMILY_MANIFEST.map((family) => [family.value, family.label])) as Record<FieldFamily, string>;

export const FIELD_ASSET_MANIFEST: Record<
  FieldAssetKey,
  {
    withAccessories: string;
    withoutAccessories: string;
  }
> = {
  'launch-party-1v0': {
    withAccessories: launchParty1v0SvgSource,
    withoutAccessories: launchParty1v0NoAccessoriesSvgSource,
  },
  'launch-party-1v1': {
    withAccessories: launchParty1v1SvgSource,
    withoutAccessories: launchParty1v1NoAccessoriesSvgSource,
  },
  'launch-party-2v0': {
    withAccessories: launchParty2v0SvgSource,
    withoutAccessories: launchParty2v0NoAccessoriesSvgSource,
  },
  'competition-field': {
    withAccessories: competitionFieldSvgSource,
    withoutAccessories: competitionFieldNoAccessoriesSvgSource,
  },
  'block-party-1v0': {
    withAccessories: blockParty1v0SvgSource,
    withoutAccessories: blockParty1v0NoAccessoriesSvgSource,
  },
  'block-party-shared': {
    withAccessories: blockPartySharedSvgSource,
    withoutAccessories: blockPartySharedNoAccessoriesSvgSource,
  },
};

export const FIELD_MANIFEST: FieldManifestEntry[] = [
  {
    family: 'launch-party',
    variant: '1v0',
    label: FIELD_VARIANT_LABELS['1v0'],
    assetKey: 'launch-party-1v0',
    accessoriesSupported: true,
  },
  {
    family: 'launch-party',
    variant: '1v1',
    label: FIELD_VARIANT_LABELS['1v1'],
    assetKey: 'launch-party-1v1',
    accessoriesSupported: true,
  },
  {
    family: 'launch-party',
    variant: '2v0',
    label: FIELD_VARIANT_LABELS['2v0'],
    assetKey: 'launch-party-2v0',
    accessoriesSupported: true,
  },
  {
    family: 'block-party',
    variant: '1v0',
    label: FIELD_VARIANT_LABELS['1v0'],
    assetKey: 'block-party-1v0',
    accessoriesSupported: true,
  },
  {
    family: 'block-party',
    variant: '1v1',
    label: FIELD_VARIANT_LABELS['1v1'],
    assetKey: 'block-party-shared',
    accessoriesSupported: true,
  },
  {
    family: 'block-party',
    variant: '2v0',
    label: FIELD_VARIANT_LABELS['2v0'],
    assetKey: 'block-party-shared',
    accessoriesSupported: true,
  },
  {
    family: 'competition-field',
    variant: 'competition',
    label: FIELD_VARIANT_LABELS.competition,
    assetKey: 'competition-field',
    accessoriesSupported: true,
  },
];

/** Each `label` is an i18next key (resolve with `t(label)`). */
export const FIELD_FAMILIES: Array<{ value: FieldFamily; label: string }> = FIELD_FAMILY_MANIFEST.slice()
  .sort((left, right) => left.order - right.order)
  .map(({ value, label }) => ({ value, label }));

export const FIELD_OPTIONS: FieldOption[] = FIELD_MANIFEST.map((field) => ({
  ...field,
  familyLabel: FIELD_FAMILY_LABELS[field.family],
}));
